﻿import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

export default function BlogIsdeEindhovenPage() {
  const { t } = useTranslation("blog-isde-eindhoven");

  const faqItems = t("faq.items", { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("schema.headline"),
    description: t("schema.description"),
    author: {
      "@type": "Organization",
      name: t("schema.author"),
      url: "https://ramen-repair-eindhoven.nl/",
    },
    publisher: {
      "@type": "Organization",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
    datePublished: t("schema.datePublished"),
    dateModified: t("schema.dateModified"),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://ramen-repair-eindhoven.nl/blog/isde-subsidie-eindhoven/",
    },
    image: t("hero.image"),
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://ramen-repair-eindhoven.nl/blog/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "ISDE-subsidie Eindhoven",
        item: "https://ramen-repair-eindhoven.nl/blog/isde-subsidie-eindhoven/",
      },
    ],
  };

  return (
    <>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <link
        rel="canonical"
        href="https://ramen-repair-eindhoven.nl/blog/isde-subsidie-eindhoven/"
      />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <Header />

      <main>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-brand-cream border-b border-brand-border">
          <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 font-geist text-sm text-brand-text/60 flex-wrap">
              <li>
                <a href="/" className="hover:text-brand-text transition-colors">
                  Home
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <a href="/blog/" className="hover:text-brand-text transition-colors">
                  Blog
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-brand-text font-medium" aria-current="page">
                ISDE-subsidie Eindhoven
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative bg-brand-cream border-b border-brand-border overflow-hidden">
          <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-10 md:py-16">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-4 md:mb-5">
                  <a
                    href="/blog/"
                    className="inline-flex items-center gap-1.5 font-geist text-sm text-brand-text/50 hover:text-brand-text transition-colors"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center text-sm"></i>
                    Blog
                  </a>
                  <span className="text-brand-text/30">·</span>
                  <span className="font-geist text-sm text-brand-text/50">
                    {t("hero.readTime")}
                  </span>
                  <span className="text-brand-text/30">·</span>
                  <span className="font-geist text-sm text-brand-text/50">
                    {t("hero.updated")}
                  </span>
                </div>
                <h1 className="font-geist font-bold text-2xl md:text-4xl text-brand-text mb-4 md:mb-6 leading-tight">
                  {t("hero.h1")}
                </h1>
                <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed max-w-[640px]">
                  {t("hero.subtitle")}
                </p>
              </div>
              <div className="w-full md:w-[380px] lg:w-[440px] flex-shrink-0">
                <div className="rounded-2xl overflow-hidden border border-brand-border">
                  <img
                    src={t("hero.image")}
                    alt="ISDE-subsidie aanvragen in Eindhoven energiezuinige ramen"
                    className="w-full h-48 md:h-64 object-cover"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* H2: ISDE Eindhoven */}
          <section id="isde-eindhoven" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.isdeEindhoven.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.isdeEindhoven.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.isdeEindhoven.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.isdeEindhoven.p3")}
            </p>
            <a
              href="/blog/isde-subsidie-2026/"
              className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
              {t("sections.isdeEindhoven.linkIsde.text")}
            </a>
          </section>

          {/* H2: Gemeente */}
          <section id="gemeente-eindhoven" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.gemeente.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.gemeente.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.gemeente.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.gemeente.p3")}
            </p>
            <a
              href="https://www.eindhoven.nl"
              rel="nofollow"
              target="_blank"
              className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-sm"></i>
              {t("sections.gemeente.linkGemeente.text")}
            </a>
          </section>

          {/* H2: Combineren */}
          <section id="subsidies-combineren" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.combineren.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.combineren.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.combineren.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed">
              {t("sections.combineren.p3")}
            </p>
          </section>

          {/* H2: Energiezuinig wonen */}
          <section id="energiezuinig-eindhoven" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.energiezuinig.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.energiezuinig.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.energiezuinig.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed">
              {t("sections.energiezuinig.p3")}
            </p>
          </section>

          {/* H2: Wij helpen */}
          <section id="wij-helpen" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.wijHelpen.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.wijHelpen.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.wijHelpen.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.wijHelpen.p3")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/glas-vervangen/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.wijHelpen.linkGlas.text")}
              </a>
              <a
                href="/kozijn-reparatie/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.wijHelpen.linkKozijn.text")}
              </a>
              <a
                href="/wijken/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.wijHelpen.linkWijken.text")}
              </a>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="pt-4 md:pt-6 mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-5 md:mb-6">
              {t("faq.h2")}
            </h2>
            <div className="flex flex-col gap-3 md:gap-4">
              {faqItems.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={idx}
                    className={`border rounded-xl overflow-hidden transition-colors ${
                      isOpen
                        ? "border-brand-yellow bg-[#FFF9E0]/30"
                        : "border-brand-border bg-white"
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-start justify-between gap-3 p-4 md:p-5 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-geist font-semibold text-sm md:text-base text-brand-text leading-snug">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full border border-brand-text/20 flex items-center justify-center mt-0.5">
                        <i
                          className={`ri-${
                            isOpen ? "subtract" : "add"
                          }-line w-4 h-4 flex items-center justify-center text-sm text-brand-text/60`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-[#0C2740] rounded-2xl p-6 md:p-10 text-white mb-10 md:mb-14">
            <h3 className="font-geist font-bold text-lg md:text-xl text-white mb-3">
              {t("cta.text")}
            </h3>
            <a
              href="tel:+31407987470"
              className="inline-flex items-center gap-2 font-geist font-bold text-sm bg-[#FFD600] text-[#0C2740] px-6 py-3 rounded-full hover:bg-[#FFD600]/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center text-sm"></i>
              {t("cta.phone")}
            </a>
          </div>

          {/* Disclaimer */}
          <div className="border-t border-brand-border pt-6 md:pt-8">
            <p className="font-geist text-xs md:text-sm text-brand-text/40 italic leading-relaxed">
              {t("disclaimer")} Laatst bijgewerkt mei 2026.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}